import { isValid, parseISO } from 'date-fns';

const MILLISECOND_DIGITS = 3;
const MAX_FRACTION_DIGITS = 12;
const MS_PER_MINUTE = 60 * 1_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

/**
 * Years below 100 have to be set after construction, since `Date.UTC` maps them
 * to 1900-1999. Building the date in a leap year keeps February 29 intact until
 * the real year is applied, carrying over any year a `24:00` wall clock rolled.
 */
const LEAP_SAFE_YEAR = 2000;

/**
 * ISO 8601 writes a date and a wall clock in either the extended
 * (`2026-09-04T18:30`) or the basic (`20260904T1830`) form, and `parseISO`
 * accepts both, so the separators are optional here as well.
 */
const CALENDAR_DATE = /^(\d{4})-?(\d{2})-?(\d{2})/;
const REDUCED_CALENDAR_DATE = /^(\d{4})-?(\d{2})?-?(\d{2})?$/;
const WALL_CLOCK = /^(\d{2}):?(\d{2})?:?(\d{2})?(?:[.,](\d+))?/;

/**
 * Payment payloads carry the due date as a calendar date in the merchant's
 * timezone. Rendering it through `parseISO` alone would re-interpret the
 * instant in the shopper's timezone and can shift the displayed day.
 *
 * The payload wall clock is therefore anchored to UTC, which has no offset
 * changes, so no shopper timezone can shift or normalize it. Rendering it back
 * requires `formatPayloadDate`, which reads the date in `PAYLOAD_TIME_ZONE`.
 */
export function parsePayloadDate(stringDate: string): Date {
  validateIsoDate(stringDate);

  const [datePart, timePart = ''] = stringDate.split(/[T ]/);
  const calendarDate = CALENDAR_DATE.exec(datePart);

  if (!calendarDate) {
    throw new Error(`Invalid ISO date: ${stringDate}`);
  }

  const [, year, month, day] = calendarDate;
  const [, hours = '0', minutes, seconds, fraction] = WALL_CLOCK.exec(timePart) ?? [];

  const anchoredDate = new Date(Date.UTC(
    LEAP_SAFE_YEAR,
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes ?? 0),
    Number(seconds ?? 0),
    getFractionMilliseconds(fraction, { minutes, seconds }),
  ));

  const yearsRolledOver = anchoredDate.getUTCFullYear() - LEAP_SAFE_YEAR;
  anchoredDate.setUTCFullYear(Number(year) + yearsRolledOver);

  return anchoredDate;
}

/**
 * The few payload fields that are timestamps rather than calendar dates, such
 * as an expiration instant, keep their offset and are read in the shopper's
 * timezone by `formatInstant`.
 *
 * A value without a time part carries no instant, so it is anchored to UTC
 * midnight like `new Date('2026-09-29')` rather than to the shopper's midnight.
 * Reduced precision is completed with the first month and day it stands for.
 */
export function parseInstant(stringDate: string): Date {
  const parsedDate = validateIsoDate(stringDate);
  const [datePart, timePart] = stringDate.split(/[T ]/);
  const calendarDate = timePart ? null : REDUCED_CALENDAR_DATE.exec(datePart);

  if (!calendarDate) {
    return parsedDate;
  }

  const [, year, month = '01', day = '01'] = calendarDate;

  return parsePayloadDate(`${year}-${month}-${day}`);
}

/**
 * ISO 8601 allows a decimal fraction on the smallest time component present, so
 * `10.5` is half past ten and `10:30.5` is thirty seconds past ten thirty.
 * Fractions are scaled with integer arithmetic and truncated to whole
 * milliseconds, so extra precision neither rounds up into the next minute nor
 * loses a millisecond to binary floating point.
 */
function getFractionMilliseconds(
  fraction: string | undefined,
  { minutes, seconds }: { minutes?: string, seconds?: string },
): number {
  if (!fraction) {
    return 0;
  }

  if (seconds) {
    return Number(fraction.padEnd(MILLISECOND_DIGITS, '0').slice(0, MILLISECOND_DIGITS));
  }

  const digits = fraction.slice(0, MAX_FRACTION_DIGITS);
  const unit = minutes ? MS_PER_MINUTE : MS_PER_HOUR;

  return Math.floor((Number(digits) * unit) / 10 ** digits.length);
}

function validateIsoDate(stringDate: string): Date {
  const parsedDate = parseISO(stringDate);

  if (!isValid(parsedDate)) {
    throw new Error(`Invalid ISO date: ${stringDate}`);
  }

  return parsedDate;
}
